import React, { createContext, useCallback, useMemo, useState } from "react";
import {
  UserPreferences,
  EvoFeedSubscriptions,
  EvoFeedSubscriptionKey,
  DEFAULT_USER_PREFERENCES
} from "../types/userPreferences";
import { fetchUserPreferences } from "../APIs/UserPreferences/GetUserPreferences";
import { saveUserPreferences } from "../APIs/UserPreferences/SaveUserPreferences";
import { errorHandler } from "../APIs/errorHandler";

export type UserPreferencesContextType = {
  preferences: UserPreferences;
  isLoading: boolean;
  isSaving: boolean;
  hasLoadError: boolean;
  loadPreferences: () => Promise<void>;
  updateEvoFeedSubscription: (
    key: EvoFeedSubscriptionKey,
    value: boolean
  ) => void;
  savePreferences: () => Promise<boolean>;
};

export const UserPreferencesContext = createContext<UserPreferencesContextType>(
  {
    preferences: DEFAULT_USER_PREFERENCES,
    isLoading: false,
    isSaving: false,
    hasLoadError: false,
    loadPreferences: async () => {},
    updateEvoFeedSubscription: () => {},
    savePreferences: async () => false
  }
);

export const UserPreferencesContextProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [preferences, setPreferences] = useState<UserPreferences>(
    DEFAULT_USER_PREFERENCES
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);

  const loadPreferences = useCallback(async () => {
    setIsLoading(true);
    setHasLoadError(false);
    try {
      const userPreferences = await fetchUserPreferences();
      setPreferences({
        ...DEFAULT_USER_PREFERENCES,
        ...userPreferences,
        evo_feed_subscriptions: {
          ...DEFAULT_USER_PREFERENCES.evo_feed_subscriptions,
          ...userPreferences.evo_feed_subscriptions
        }
      });
    } catch (error) {
      setHasLoadError(true);
      errorHandler(error, "Failed to load user preferences");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateEvoFeedSubscription = useCallback(
    (key: EvoFeedSubscriptionKey, value: boolean) => {
      setPreferences((prev) => {
        const updatedSubscriptions: EvoFeedSubscriptions = {
          ...prev.evo_feed_subscriptions,
          [key]: value
        };

        // If subscribing to all auto-confirmed bookings,
        // force same-day auto-confirmed bookings to true as well
        if (key === "new_autoconfirmed_bookings" && value) {
          updatedSubscriptions.new_same_day_autoconfirmed_bookings = true;
        }

        return {
          ...prev,
          evo_feed_subscriptions: updatedSubscriptions
        };
      });
    },
    []
  );

  const savePreferences = useCallback(async (): Promise<boolean> => {
    setIsSaving(true);
    try {
      const savedPreferences = await saveUserPreferences(preferences);
      setPreferences({
        ...DEFAULT_USER_PREFERENCES,
        ...savedPreferences,
        evo_feed_subscriptions: {
          ...DEFAULT_USER_PREFERENCES.evo_feed_subscriptions,
          ...savedPreferences.evo_feed_subscriptions
        }
      });
      return true;
    } catch (error) {
      errorHandler(error, "Failed to save user preferences");
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [preferences]);

  const contextValue = useMemo(
    () => ({
      preferences,
      isLoading,
      isSaving,
      hasLoadError,
      loadPreferences,
      updateEvoFeedSubscription,
      savePreferences
    }),
    [
      preferences,
      isLoading,
      isSaving,
      hasLoadError,
      loadPreferences,
      updateEvoFeedSubscription,
      savePreferences
    ]
  );

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      {children}
    </UserPreferencesContext.Provider>
  );
};
